import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box, Typography, Button, TextField, MenuItem, Select, FormControl,
  InputLabel, ToggleButton, ToggleButtonGroup, Checkbox, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Alert, CircularProgress, Divider, InputAdornment, Drawer, Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import ZephyrIconWhite from "@/Images/Icons/Zephyr_Icon_White.png";

const ZEPHYR_BLUE = "#2baede";
const ZEPHYR_PURPLE = "#38184f";
const ZEPHYR_LIGHT = "#f3eefa";

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmt = (v) => (v != null && v !== "" ? `$${Number(v).toFixed(2)}` : "—");
const today = () => new Date().toISOString().slice(0, 10);
const approxEqual = (a, b) => Math.abs(a - b) < 0.005;

function findUniqueSubset(invoices, target) {
  const items = invoices.filter((inv) => Number(inv.InvoiceValue) <= target);
  let found = null;
  let count = 0;
  function search(idx, remaining, current) {
    if (approxEqual(remaining, 0)) { count++; if (count === 1) found = [...current]; return; }
    if (idx >= items.length || remaining < 0 || count > 1) return;
    search(idx + 1, remaining - Number(items[idx].InvoiceValue), [...current, items[idx]]);
    search(idx + 1, remaining, current);
  }
  search(0, target, []);
  return count === 1 ? found : null;
}

// ─── Shared UI ───────────────────────────────────────────────────────────────

function SectionLabel({ children, sx }) {
  return (
    <Typography variant="overline" sx={{ color: ZEPHYR_PURPLE, fontWeight: 700, letterSpacing: 1.2, display: "block", ...sx }}>
      {children}
    </Typography>
  );
}

function ZBtn({ children, variant = "contained", sx, ...props }) {
  const base = variant === "contained"
    ? { background: `linear-gradient(135deg, ${ZEPHYR_PURPLE}, #7c3aed)`, color: "#fff", "&:hover": { background: `linear-gradient(135deg, #3b0764, ${ZEPHYR_PURPLE})` } }
    : { borderColor: ZEPHYR_PURPLE, color: ZEPHYR_PURPLE, "&:hover": { borderColor: "#3b0764", backgroundColor: "#f5f0ff" } };
  return (
    <Button variant={variant} sx={{ textTransform: "none", fontWeight: 600, ...base, ...sx }} {...props}>
      {children}
    </Button>
  );
}

// ─── Underpaid Modal ─────────────────────────────────────────────────────────

function UnderpaidModal({ open, invoiceID, initialAmounts = {}, onClose, onConfirm }) {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [amounts, setAmounts] = useState({});

  useEffect(() => {
    if (!open || !invoiceID) return;
    setLoading(true); setAmounts(initialAmounts);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_INVOICE_SCRIPTS}/${invoiceID}`, "GET")
      .then((res) => setScripts(res?.data ?? []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [open, invoiceID]);

  const totalExpected = scripts.reduce((s, sc) => s + Number(sc.InvAmount ?? 0), 0);
  const totalPaid = scripts.reduce((s, sc) => {
    const v = amounts[sc.ScriptID];
    return s + (v !== undefined && v !== "" ? Number(v) : 0);
  }, 0);
  const diff = totalPaid - totalExpected;

  const handleConfirm = () => {
    const paid = [], onHold = [];
    for (const s of scripts) {
      const v = amounts[s.ScriptID];
      (v !== undefined && v !== "" && Number(v) > 0)
        ? paid.push({ scriptID: s.ScriptID, allocatedAmount: Number(v), invoiceID })
        : onHold.push(s.ScriptID);
    }
    onConfirm({ invoiceID, paid, onHold });
    onClose();
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU") : "—";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{ color: ZEPHYR_PURPLE, fontWeight: 700, pb: 1 }}>
        Underpaid — Invoice #{invoiceID}
        <IconButton sx={{ position: "absolute", right: 8, top: 8 }} onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={28} sx={{ color: ZEPHYR_PURPLE }} /></Box> : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the amount received per script. Leave blank to mark as on-hold.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Dispensed</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Invoiced</TableCell>
                    <TableCell align="right">Received ($)</TableCell>
                    <TableCell sx={{ width: "1px" }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scripts.map((s) => (
                    <TableRow key={s.ScriptID}>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(s.DateDispensed)}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.txtItem}</TableCell>
                      <TableCell align="right">{fmt(s.InvAmount)}</TableCell>
                      <TableCell align="right">
                        <TextField size="small" type="number" placeholder="blank = on-hold"
                          value={amounts[s.ScriptID] ?? ""}
                          onChange={(e) => setAmounts((p) => ({ ...p, [s.ScriptID]: e.target.value }))}
                          inputProps={{ min: 0, step: "0.01" }} sx={{ width: 120 }} />
                      </TableCell>
                      <TableCell sx={{ width: "1px", pl: 0.5 }}>
                        <Tooltip title="Set to invoice amount">
                          <Button size="small" onClick={() => setAmounts((p) => ({ ...p, [s.ScriptID]: String(Number(s.InvAmount).toFixed(2)) }))}
                            sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: "0.7rem", color: ZEPHYR_BLUE, borderColor: ZEPHYR_BLUE, textTransform: "none" }}
                            variant="outlined">
                            = Invoice
                          </Button>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Totals summary */}
            {scripts.length > 0 && (
              <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px solid #e2e8f0", display: "flex", flexDirection: "column", gap: 0.5 }}>
                {[
                  ["Total Expected", fmt(totalExpected), "#1a1a1a"],
                  ["Total Received", fmt(totalPaid), ZEPHYR_PURPLE],
                  [
                    diff < -0.005 ? "Shortfall" : diff > 0.005 ? "Overpaid" : "Balanced",
                    diff < -0.005 ? `-${fmt(Math.abs(diff))}` : diff > 0.005 ? `+${fmt(diff)}` : "✓",
                    diff < -0.005 ? "#dc2626" : diff > 0.005 ? "#d97706" : "#16a34a",
                  ],
                ].map(([label, val, color]) => (
                  <Box key={label} sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                    <Typography fontWeight={700} sx={{ color }}>{val}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <ZBtn onClick={handleConfirm} disabled={loading}>Confirm</ZBtn>
      </DialogActions>
    </Dialog>
  );
}

// ─── Pending Scripts Modal ────────────────────────────────────────────────────

function PendingScriptsModal({ open, insCompID, employerID, attachedScripts, onClose, onAddScript }) {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const hasPayer = insCompID || employerID;

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const p = new URLSearchParams();
    if (insCompID) p.set("insCompID", insCompID);
    if (employerID) p.set("employerID", employerID);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_PENDING_SCRIPTS}?${p}`, "GET")
      .then((res) => setScripts(res?.data ?? []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [open, insCompID, employerID]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{ color: ZEPHYR_PURPLE, fontWeight: 700, pb: 1 }}>
        Pending Scripts (On-Hold)
        <IconButton sx={{ position: "absolute", right: 8, top: 8 }} onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {!hasPayer && <Alert severity="info" sx={{ mb: 2 }}>No payer selected — showing all pending. Select a payer to enable adding.</Alert>}
        {loading ? <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={28} sx={{ color: ZEPHYR_PURPLE }} /></Box>
          : scripts.length === 0 ? <Typography color="text.secondary" sx={{ textAlign: "center", py: 3 }}>No pending scripts found.</Typography>
          : (
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Script ID</TableCell><TableCell>Invoice</TableCell>
                    <TableCell>Patient</TableCell><TableCell>Item</TableCell>
                    <TableCell align="right">Amount</TableCell><TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scripts.map((s) => {
                    const added = attachedScripts.has(s.ScriptID);
                    return (
                      <TableRow key={s.ScriptID}>
                        <TableCell>{s.ScriptID}</TableCell><TableCell>{s.InvoiceID}</TableCell>
                        <TableCell>{s.FullName}</TableCell>
                        <TableCell sx={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.txtItem}</TableCell>
                        <TableCell align="right">{fmt(s.InvAmount)}</TableCell>
                        <TableCell align="right">
                          <Button size="small" variant="outlined" disabled={!hasPayer || added}
                            onClick={() => onAddScript(s)}
                            sx={{ textTransform: "none", borderColor: ZEPHYR_PURPLE, color: ZEPHYR_PURPLE }}>
                            {added ? "Added" : "Add"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Automatch Modal ──────────────────────────────────────────────────────────

function AutomatchModal({ open, matchedInvoices, onClose, onConfirm }) {
  const total = matchedInvoices?.reduce((s, inv) => s + Number(inv.InvoiceValue), 0) ?? 0;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{ color: ZEPHYR_PURPLE, fontWeight: 700 }}>
        Automatch Found
        <IconButton sx={{ position: "absolute", right: 8, top: 8 }} onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>These invoices sum exactly to the remittance total:</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice ID</TableCell><TableCell>Patient</TableCell><TableCell align="right">Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(matchedInvoices ?? []).map((inv) => (
                <TableRow key={inv.InvoiceID}>
                  <TableCell>{inv.InvoiceID}</TableCell><TableCell>{inv.FullName}</TableCell>
                  <TableCell align="right">{fmt(inv.InvoiceValue)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(total)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <ZBtn onClick={onConfirm}>Attach All</ZBtn>
      </DialogActions>
    </Dialog>
  );
}

// ─── Reconcile Confirmation Modal ─────────────────────────────────────────────

function ReconcileModal({ open, totalAmount, allocatedTotal, payerName, payDate, referenceNumber, onClose, onConfirm, loading }) {
  const balanced = approxEqual(totalAmount, allocatedTotal);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU") : "—";
  const rows = [
    payerName     && ["Payer",            payerName],
    payDate       && ["Date",             fmtDate(payDate)],
    referenceNumber && ["Reference",      referenceNumber],
                     ["Remittance total", fmt(totalAmount)],
                     ["Allocated",        fmt(allocatedTotal)],
  ].filter(Boolean);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{ color: ZEPHYR_PURPLE, fontWeight: 700 }}>
        Confirm Reconcile Payment
        <IconButton sx={{ position: "absolute", right: 8, top: 8 }} onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {!balanced
          ? <Alert severity="error" sx={{ mb: 2 }}>Allocated ({fmt(allocatedTotal)}) ≠ Remittance total ({fmt(totalAmount)}).</Alert>
          : <Alert severity="success" sx={{ mb: 2 }}>Totals match — ready to submit.</Alert>}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {rows.map(([label, val]) => (
            <Box key={label} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">{label}</Typography>
              <Typography fontWeight={700} sx={{ textAlign: "right" }}>{val}</Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} sx={{ textTransform: "none" }}>Cancel</Button>
        <ZBtn onClick={onConfirm} disabled={!balanced || loading}>
          {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Submit"}
        </ZBtn>
      </DialogActions>
    </Dialog>
  );
}

// ─── Invoice Detail Modal ────────────────────────────────────────────────────

function InvoiceDetailModal({ open, invoiceID, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU") : "—";

  useEffect(() => {
    if (!open || !invoiceID) return;
    setLoading(true);
    setData(null);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_INVOICE_DETAILS}/${invoiceID}`, "GET")
      .then((res) => setData(res?.data ?? null))
      .finally(() => setLoading(false));
  }, [open, invoiceID]);

  const scripts = data?.scripts ?? [];
  const c = data?.claim;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${ZEPHYR_PURPLE}, ${ZEPHYR_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5,

      }}>
        <span>Invoice #{invoiceID}{c?.FullName ? ` — ${c.FullName}` : ""}</span>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress sx={{ color: ZEPHYR_PURPLE }} />
          </Box>
        ) : (
          <>
            {c && (
              <Box sx={{
                display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1.5, mb: 2,
                background: `${ZEPHYR_PURPLE}08`, borderRadius: 1.5, p: 1.5,
              }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Patient</Typography>
                  <Typography variant="body2" fontWeight={600}>{c.FullName}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Claim #</Typography>
                  <Typography variant="body2">{c.ClaimNumber}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Chemist</Typography>
                  <Typography variant="body2">{c.ChemistName || c.ChemShortCode || "—"}</Typography>
                </Box>
              </Box>
            )}
            <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1 }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: `${ZEPHYR_PURPLE}08` }}>
                  <TableRow>
                    {[
                      { label: "Script ID", align: "left" },
                      { label: "Date", align: "left" },
                      { label: "Item", align: "left" },
                      { label: "Qty", align: "center" },
                      { label: "Invoiced", align: "right" },
                      { label: "Status", align: "center" },
                    ].map(({ label, align }) => (
                      <TableCell key={label} align={align} sx={{ fontWeight: 700, fontSize: "0.78rem", color: ZEPHYR_PURPLE, py: 1 }}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scripts.map((s) => {
                    let bg = "transparent";
                    if (s.PaymentID) bg = "#dcfce7";
                    else if (Number(s.WrittenOff)) bg = "#fee2e2";
                    else if (Number(s.OnHold) === -1) bg = "#fef9c3";
                    let chip = null;
                    if (s.PaymentID) chip = <Chip label={`Paid #${s.PaymentID}`} color="success" size="small" sx={{ fontSize: "0.72rem" }} />;
                    else if (Number(s.WrittenOff)) chip = <Chip label="Written Off" color="error" size="small" sx={{ fontSize: "0.72rem" }} />;
                    else if (Number(s.OnHold) === -1) chip = <Chip label="Missed" color="warning" size="small" sx={{ fontSize: "0.72rem" }} />;
                    return (
                      <TableRow key={s.ScriptID} sx={{ backgroundColor: bg, "& td": { py: 0.6, px: 1, fontSize: "0.8rem" } }}>
                        <TableCell>{s.ScriptID}</TableCell>
                        <TableCell>{fmtDate(s.DateDispensed)}</TableCell>
                        <TableCell>{s.txtItem}</TableCell>
                        <TableCell align="center">{s.Qty ?? "—"}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(s.InvAmount)}</TableCell>
                        <TableCell align="center">{chip}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Remittance Drawer ────────────────────────────────────────────────────────

const EMPTY_HEADER = {
  payerType: "insurer",
  payerId: null,
  remittanceDate: today(),
  referenceNumber: "",
  paymentComment: "",
  totalAmount: "",
  chemDirect: false,
};

const EMPTY_FILTERS = {
  patientName: "",
  employerId: null,
  insurerId: null,
  invoiceId: "",
  minValue: "",
  maxValue: "",
};

export function RemittanceDrawer({ open, onClose }) {
  const [header, setHeader] = useState(EMPTY_HEADER);
  const setH = (key, val) => setHeader((p) => ({ ...p, [key]: val }));

  const [insurers, setInsurers] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [payerOptionsLoading, setPayerOptionsLoading] = useState(false);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const setF = (key, val) => setFilters((p) => ({ ...p, [key]: val }));

  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  const [attachedScripts, setAttachedScripts] = useState(new Map());
  const [onHoldScriptIds, setOnHoldScriptIds] = useState(new Set());

  const [underpaidInvoiceID, setUnderpaidInvoiceID] = useState(null);
  const [underpaidInitialAmounts, setUnderpaidInitialAmounts] = useState({});
  const [invoiceDetailID, setInvoiceDetailID] = useState(null);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [automatchInvoices, setAutomatchInvoices] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [pendingSubmitAction, setPendingSubmitAction] = useState(null);

  const totalRef = useRef(null);
  const prevBalanced = useRef(false);

  // Auto-focus total on open
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => totalRef.current?.focus(), 220);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Load payer options once
  useEffect(() => {
    if (!open || insurers.length > 0) return;
    setPayerOptionsLoading(true);
    Promise.all([
      phappsRequest(API_PATHS.ZEPHYR_ADMIN_INSURERS, "GET"),
      phappsRequest(API_PATHS.ZEPHYR_ADMIN_EMPLOYERS, "GET"),
    ])
      .then(([ir, er]) => { setInsurers(ir?.data ?? []); setEmployers(er?.data ?? []); })
      .catch(console.error)
      .finally(() => setPayerOptionsLoading(false));
  }, [open]);

  // Mirror payer into filter dropdowns
  useEffect(() => {
    if (header.payerType === "insurer") { setF("insurerId", header.payerId || null); setF("employerId", null); }
    else if (header.payerType === "employer") { setF("employerId", header.payerId || null); setF("insurerId", null); }
  }, [header.payerType, header.payerId]);

  // Server-side fetch: payer + patientName
  useEffect(() => {
    setInvoicesLoading(true);
    const p = new URLSearchParams();
    if (filters.insurerId) p.set("insCompID", filters.insurerId);
    if (filters.employerId) p.set("employerID", filters.employerId);
    if (filters.patientName) p.set("patientName", filters.patientName);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_OUTSTANDING_INVOICES}?${p}`, "GET")
      .then((res) => setInvoices(res?.data ?? []))
      .catch(console.error)
      .finally(() => setInvoicesLoading(false));
  }, [filters.insurerId, filters.employerId, filters.patientName]);

  // Client-side: invoiceId starts-with + value range
  const displayedInvoices = useMemo(() =>
    invoices.filter((inv) => {
      if (filters.invoiceId && !String(inv.InvoiceID).startsWith(filters.invoiceId)) return false;
      if (filters.minValue !== "" && filters.minValue != null && Number(inv.InvoiceValue) < Number(filters.minValue)) return false;
      if (filters.maxValue !== "" && filters.maxValue != null && Number(inv.InvoiceValue) > Number(filters.maxValue)) return false;
      return true;
    }), [invoices, filters.invoiceId, filters.minValue, filters.maxValue]);

  const allocatedTotal = useMemo(() => { let s = 0; for (const sc of attachedScripts.values()) s += sc.allocatedAmount; return s; }, [attachedScripts]);
  const totalAmount = header.totalAmount !== "" ? Number(header.totalAmount) : 0;
  const remaining = totalAmount - allocatedTotal;
  const balanced = totalAmount > 0 && approxEqual(totalAmount, allocatedTotal);

  // Auto-open reconcile when totals first balance
  useEffect(() => {
    if (balanced && !prevBalanced.current && attachedScripts.size > 0) setReconcileOpen(true);
    prevBalanced.current = balanced;
  }, [balanced, attachedScripts.size]);

  const attachedByInvoice = useMemo(() => {
    const m = new Map();
    for (const s of attachedScripts.values()) {
      const ex = m.get(s.invoiceID);
      m.set(s.invoiceID, { invoiceID: s.invoiceID, total: (ex?.total ?? 0) + s.allocatedAmount });
    }
    return Array.from(m.values());
  }, [attachedScripts]);


  // ── Actions ──────────────────────────────────────────────────────────────

  const addFullInvoice = async (invoice) => {
    try {
      const res = await phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_INVOICE_SCRIPTS}/${invoice.InvoiceID}`, "GET");
      const scripts = res?.data ?? [];
      setAttachedScripts((prev) => {
        const next = new Map(prev);
        for (const s of scripts) next.set(s.ScriptID, { scriptID: s.ScriptID, allocatedAmount: Number(s.InvAmount), invoiceID: invoice.InvoiceID });
        return next;
      });
      if (!header.payerId) {
        if (invoice.InsCompID) { setH("payerType", "insurer"); setH("payerId", invoice.InsCompID); }
        else if (invoice.EmployerID) { setH("payerType", "employer"); setH("payerId", invoice.EmployerID); }
      }
    } catch (err) { console.error(err); }
  };

  const removeInvoiceGroup = (invoiceID) =>
    setAttachedScripts((prev) => {
      const next = new Map(prev);
      for (const [id, s] of next) if (s.invoiceID === invoiceID) next.delete(id);
      return next;
    });

  const handleUnderpaidConfirm = ({ invoiceID, paid, onHold }) => {
    setAttachedScripts((prev) => {
      const next = new Map(prev);
      for (const [id, s] of next) if (s.invoiceID === invoiceID) next.delete(id);
      for (const s of paid) next.set(s.scriptID, { scriptID: s.scriptID, allocatedAmount: s.allocatedAmount, invoiceID });
      return next;
    });
    setOnHoldScriptIds((prev) => { const next = new Set(prev); onHold.forEach((id) => next.add(id)); return next; });
  };

  const handleAddPendingScript = (script) =>
    setAttachedScripts((prev) => {
      const next = new Map(prev);
      next.set(script.ScriptID, { scriptID: script.ScriptID, allocatedAmount: Number(script.InvAmount), invoiceID: script.InvoiceID });
      return next;
    });

  const handlePayerTypeChange = (_, newType) => {
    if (!newType) return;
    if (attachedScripts.size > 0 && !window.confirm("Changing payer will clear all attached invoices. Continue?")) return;
    setAttachedScripts(new Map()); setOnHoldScriptIds(new Set());
    setH("payerType", newType); setH("payerId", null);
  };

  const resetForm = (keepPayer = false) => {
    setHeader(keepPayer ? { ...EMPTY_HEADER, payerType: header.payerType, payerId: header.payerId } : { ...EMPTY_HEADER });
    setFilters({ ...EMPTY_FILTERS });
    setAttachedScripts(new Map()); setOnHoldScriptIds(new Set());
    setSubmitError(null); setSubmitSuccess(null);
  };

  const doSubmit = async (action) => {
    if (!balanced) { setSubmitError("Allocated total must equal remittance total."); return; }
    setSubmitLoading(true); setSubmitError(null);
    try {
      const res = await phappsRequest(API_PATHS.ZEPHYR_ADMIN_CREATE_REMITTANCE, "POST", {
        payDate: header.remittanceDate,
        paymentComment: header.paymentComment || null,
        referenceNumber: header.referenceNumber || null,
        insCompID: header.payerType === "insurer" ? header.payerId : null,
        employerID: header.payerType === "employer" ? header.payerId : null,
        scripts: Array.from(attachedScripts.values()).map((s) => ({
          scriptID: s.scriptID,
          rcvdAmount: s.allocatedAmount,
        })),
      });
      setReconcileOpen(false);
      const paymentID = res?.data?.PaymentID;
      if (action === "close") { onClose(); resetForm(); }
      else if (action === "again") { resetForm(false); setSubmitSuccess(`Payment #${paymentID} recorded.`); }
      else if (action === "again-same-payer") { resetForm(true); setSubmitSuccess(`Payment #${paymentID} recorded.`); }
    } catch (err) {
      setSubmitError(err?.message ?? "Failed to submit."); setSubmitLoading(false);
      return;
    }
    setSubmitLoading(false);
  };

  const handleSubmitClick = (action) => {
    if (!balanced) { setSubmitError("Allocated total must equal remittance total."); return; }
    setPendingSubmitAction(action);
    setReconcileOpen(true);
  };

  const payerOptions = header.payerType === "insurer" ? insurers : employers;
  const payerLabelKey = header.payerType === "insurer" ? "InsCompName" : "EmployerName";
  const payerIdKey = header.payerType === "insurer" ? "InsCompID" : "EmployerID";
  const selectedPayerName = useMemo(() => {
    if (!header.payerId) return null;
    return payerOptions.find((o) => o[payerIdKey] === header.payerId)?.[payerLabelKey] ?? null;
  }, [header.payerId, payerOptions, payerIdKey, payerLabelKey]);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100vw", sm: "95vw", md: 1200 },
            maxWidth: "100vw",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* ── Drawer header bar ─────────────────────────────────────── */}
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 3, py: 1.5, flexShrink: 0,
          background: `linear-gradient(135deg, ${ZEPHYR_PURPLE} 0%, #2baede 100%)`,
          color: "#fff",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box component="img" src={ZephyrIconWhite} alt="Zephyr Claims" sx={{ height: 28, width: "auto" }} />
            <Typography fontWeight={700} fontSize="1rem">Reconcile Payment</Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "#fff" }} size="small"><CloseIcon /></IconButton>
        </Box>

        {/* ── Scrollable body ───────────────────────────────────────── */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, pt: 2, pb: 1, display: "flex", flexDirection: "column", gap: 2 }}>

          {submitError && <Alert severity="error" onClose={() => setSubmitError(null)}>{submitError}</Alert>}
          {submitSuccess && <Alert severity="success" onClose={() => setSubmitSuccess(null)}>{submitSuccess}</Alert>}

          {/* ── Compact header form — 2-row grid ────────────────────── */}
          <Box sx={{ backgroundColor: ZEPHYR_LIGHT, border: "1px solid #c4b5fd", borderRadius: 1.5, p: 1.5 }}>
            {/*
              Grid layout (matches screenshot):
              Row 1: [toggle] [payer dropdown] [reference]   [comment ← spans 2 rows]
              Row 2: [total ] [date          ] [paid direct] [                       ]
            */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "auto minmax(180px, 1fr) minmax(160px, 1fr) minmax(160px, 1fr)",
                gridTemplateRows: "auto auto",
                columnGap: 1.5,
                rowGap: 1,
              }}
            >
              {/* R1 C1 — Payer type toggle */}
              <ToggleButtonGroup
                value={header.payerType}
                exclusive
                onChange={handlePayerTypeChange}
                size="small"
                sx={{ alignSelf: "center", gridRow: 1, gridColumn: 1 }}
              >
                {["insurer", "employer"].map((type) => (
                  <ToggleButton key={type} value={type} sx={{
                    textTransform: "none", fontWeight: 600, px: 2, py: 0.5,
                    "&.Mui-selected": { backgroundColor: ZEPHYR_PURPLE, color: "#fff", "&:hover": { backgroundColor: "#3b0764" } },
                  }}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              {/* R1 C2 — Payer dropdown */}
              <FormControl size="small" sx={{ alignSelf: "center", gridRow: 1, gridColumn: 2 }}>
                <InputLabel sx={{ fontSize: "0.85rem" }}>
                  {header.payerType === "insurer" ? "Insurer" : "Employer"}
                </InputLabel>
                <Select
                  label={header.payerType === "insurer" ? "Insurer" : "Employer"}
                  value={header.payerId ?? ""}
                  onChange={(e) => setH("payerId", e.target.value || null)}
                  disabled={payerOptionsLoading}
                  sx={{ fontSize: "0.85rem" }}
                >
                  <MenuItem value="">— Select —</MenuItem>
                  {payerOptions.map((opt) => (
                    <MenuItem key={opt[payerIdKey]} value={opt[payerIdKey]}>{opt[payerLabelKey]}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* R1 C3 — Reference number */}
              <TextField
                label="Reference number"
                size="small"
                value={header.referenceNumber}
                onChange={(e) => setH("referenceNumber", e.target.value)}
                sx={{ alignSelf: "center", gridRow: 1, gridColumn: 3 }}
              />

              {/* R1–R2 C4 — Payment comment (spans both rows) */}
              <TextField
                label="Payment comment"
                size="small"
                multiline
                value={header.paymentComment}
                onChange={(e) => setH("paymentComment", e.target.value)}
                sx={{
                  gridRow: "1 / 3",
                  gridColumn: 4,
                  "& .MuiOutlinedInput-root": { height: "100%", alignItems: "flex-start" },
                  "& .MuiInputBase-inputMultiline": { height: "100% !important", overflow: "auto !important" },
                }}
              />

              {/* R2 C1 — Remittance total (uncontrolled: commits on blur only, no re-render while typing) */}
              <TextField
                key={header.totalAmount === "" ? "reset" : "active"}
                inputRef={totalRef}
                label="Remittance total"
                type="number"
                size="small"
                defaultValue={header.totalAmount}
                onBlur={(e) => setH("totalAmount", e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                inputProps={{ min: 0, step: "0.01", tabIndex: 1 }}
                sx={{ alignSelf: "center", gridRow: 2, gridColumn: 1 }}
              />

              {/* R2 C2 — Remittance date */}
              <TextField
                label="Remittance date"
                type="date"
                size="small"
                value={header.remittanceDate}
                onChange={(e) => setH("remittanceDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ tabIndex: 3 }}
                sx={{ alignSelf: "center", gridRow: 2, gridColumn: 2 }}
              />

              {/* R2 C3 — Paid Direct */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={header.chemDirect}
                    onChange={(e) => setH("chemDirect", e.target.checked)}
                    size="small"
                    sx={{ color: ZEPHYR_PURPLE, "&.Mui-checked": { color: ZEPHYR_PURPLE } }}
                  />
                }
                label={<Typography variant="body2" sx={{ fontSize: "0.82rem" }}>Paid Direct</Typography>}
                sx={{ ml: 0, alignSelf: "center", gridRow: 2, gridColumn: 3 }}
              />
            </Box>
          </Box>

          {/* ── Main workspace ───────────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 280px" }, gap: 2, alignItems: "start", flex: 1 }}>

            {/* Outstanding invoices panel */}
            <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden" }}>
              {/* Panel toolbar */}
              <Box sx={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                px: 2, py: 1, borderBottom: "1px solid #e2e8f0", backgroundColor: "#f8fafc",
                flexWrap: "wrap", gap: 1,
              }}>
                <SectionLabel sx={{ mb: 0 }}>Outstanding Invoices</SectionLabel>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<AutoFixHighIcon sx={{ fontSize: "0.9rem !important" }} />}
                    onClick={() => {
                      if (!totalAmount || displayedInvoices.length === 0) return;
                      const match = findUniqueSubset(displayedInvoices, totalAmount);
                      if (match) setAutomatchInvoices(match);
                    }}
                    disabled={!totalAmount || displayedInvoices.length === 0}
                    sx={{ textTransform: "none", fontSize: "0.78rem", borderColor: ZEPHYR_BLUE, color: ZEPHYR_BLUE, py: 0.5, px: 1.5 }}>
                    Find Match
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<PendingActionsIcon sx={{ fontSize: "0.9rem !important" }} />}
                    onClick={() => setPendingOpen(true)}
                    sx={{ textTransform: "none", fontSize: "0.78rem", borderColor: ZEPHYR_PURPLE, color: ZEPHYR_PURPLE, py: 0.5, px: 1.5 }}>
                    Pending
                  </Button>
                </Box>
              </Box>

              {/* Filters bar */}
              <Box sx={{ px: 2, py: 1, borderBottom: "1px solid #e2e8f0", backgroundColor: "#fafafa", display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                <TextField
                  label="Invoice ID" size="small" value={filters.invoiceId}
                  onChange={(e) => setF("invoiceId", e.target.value)}
                  placeholder="starts with…" inputProps={{ tabIndex: 2 }}
                  sx={{ width: 130 }}
                  InputProps={{ sx: { fontSize: "0.82rem" } }}
                  onKeyDown={(e) => {
                    if (e.ctrlKey && displayedInvoices.length > 0) {
                      e.preventDefault();
                      addFullInvoice(displayedInvoices[0]);
                      setF("invoiceId", "");
                    }
                  }}
                />
                <TextField
                  label="Patient" size="small" value={filters.patientName}
                  onChange={(e) => setF("patientName", e.target.value)}
                  sx={{ flex: 1, minWidth: 140 }}
                  InputProps={{ sx: { fontSize: "0.82rem" } }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel sx={{ fontSize: "0.82rem" }}>Insurer</InputLabel>
                  <Select label="Insurer" value={filters.insurerId ?? ""} onChange={(e) => setF("insurerId", e.target.value || null)} sx={{ fontSize: "0.82rem" }}>
                    <MenuItem value="">— Any —</MenuItem>
                    {insurers.map((i) => <MenuItem key={i.InsCompID} value={i.InsCompID}>{i.InsCompName}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel sx={{ fontSize: "0.82rem" }}>Employer</InputLabel>
                  <Select label="Employer" value={filters.employerId ?? ""} onChange={(e) => setF("employerId", e.target.value || null)} sx={{ fontSize: "0.82rem" }}>
                    <MenuItem value="">— Any —</MenuItem>
                    {employers.map((e) => <MenuItem key={e.EmployerID} value={e.EmployerID}>{e.EmployerName}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField label="Min $" size="small" type="number" value={filters.minValue}
                  onChange={(e) => setF("minValue", e.target.value)}
                  inputProps={{ min: 0, step: "0.01" }} sx={{ width: 90 }}
                  InputProps={{ sx: { fontSize: "0.82rem" } }}
                />
                <TextField label="Max $" size="small" type="number" value={filters.maxValue}
                  onChange={(e) => setF("maxValue", e.target.value)}
                  inputProps={{ min: 0, step: "0.01" }} sx={{ width: 90 }}
                  InputProps={{ sx: { fontSize: "0.82rem" } }}
                />
                <Button size="small" onClick={() => setFilters((p) => ({ ...p, patientName: "", invoiceId: "", minValue: "", maxValue: "" }))}
                  sx={{ textTransform: "none", color: "#94a3b8", fontSize: "0.78rem", ml: "auto" }}>
                  Clear
                </Button>
              </Box>

              {/* Invoice table */}
              {invoicesLoading ? (
                <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={24} sx={{ color: ZEPHYR_PURPLE }} /></Box>
              ) : displayedInvoices.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: "center", py: 4, fontSize: "0.875rem" }}>No outstanding invoices.</Typography>
              ) : (
                <TableContainer sx={{ maxHeight: "calc(100vh - 420px)" }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ "& th": { backgroundColor: "#f1f5f9", fontWeight: 700, fontSize: "0.78rem", py: 0.75, px: 1 } }}>
                        <TableCell sx={{ width: 80 }}>Invoice</TableCell>
                        <TableCell>Patient</TableCell>
                        <TableCell sx={{ width: 90 }}>Insurer</TableCell>
                        <TableCell sx={{ width: 110 }}>Employer</TableCell>
                        <TableCell sx={{ width: 80 }}>Claim #</TableCell>
                        <TableCell align="right" sx={{ width: 80 }}>Value</TableCell>
                        <TableCell sx={{ width: 130 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayedInvoices.map((inv) => {
                        const anyAttached = attachedByInvoice.some((a) => a.invoiceID === inv.InvoiceID);
                        return (
                          <TableRow key={inv.InvoiceID}
                            sx={{
                              backgroundColor: anyAttached ? "#f0fdf4" : "inherit",
                              "&:hover": { backgroundColor: anyAttached ? "#dcfce7" : "#f8fafc" },
                              "& td": { py: 0.6, px: 1, fontSize: "0.82rem" },
                            }}
                          >
                            <TableCell sx={{ fontWeight: anyAttached ? 700 : 500 }}>
                              <button onClick={() => setInvoiceDetailID(inv.InvoiceID)}
                                style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                                  color: ZEPHYR_PURPLE, fontWeight: "inherit", fontSize: "inherit", textDecoration: "underline" }}>
                                {inv.InvoiceID}
                              </button>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {inv.FullName}
                            </TableCell>
                            <TableCell>
                              {inv.InsCompShortName ? (
                                <Tooltip title={inv.InsCompName ?? inv.InsCompShortName} placement="top">
                                  <Chip label={inv.InsCompShortName} size="small"
                                    sx={{ height: 20, fontSize: "0.7rem", backgroundColor: "#ede9fe", color: ZEPHYR_PURPLE, fontWeight: 600, cursor: "default" }} />
                                </Tooltip>
                              ) : "—"}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#475569" }}>
                              {inv.EmployerName ?? "—"}
                            </TableCell>
                            <TableCell sx={{ color: "#475569" }}>{inv.ClaimNumber ?? "—"}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(inv.InvoiceValue)}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                              <Button size="small" onClick={() => addFullInvoice(inv)}
                                sx={{ textTransform: "none", mr: 0.5, px: 1, minWidth: 0, py: 0.25,
                                  backgroundColor: ZEPHYR_PURPLE, color: "#fff", fontSize: "0.7rem",
                                  "&:hover": { backgroundColor: "#3b0764" } }}>
                                Add
                              </Button>
                              <Button size="small" variant="outlined" onClick={() => { setUnderpaidInitialAmounts({}); setUnderpaidInvoiceID(inv.InvoiceID); }}
                                sx={{ textTransform: "none", px: 1, minWidth: 0, py: 0.25,
                                  borderColor: ZEPHYR_PURPLE, color: ZEPHYR_PURPLE, fontSize: "0.7rem" }}>
                                Underpaid
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            {/* Attached invoices panel */}
            <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden" }}>
              <Box sx={{ px: 2, py: 1, borderBottom: "1px solid #e2e8f0", backgroundColor: "#f8fafc" }}>
                <SectionLabel sx={{ mb: 0 }}>Attached</SectionLabel>
              </Box>

              {attachedByInvoice.length === 0 ? (
                <Typography color="text.secondary" sx={{ fontSize: "0.82rem", textAlign: "center", py: 3, px: 2 }}>
                  None attached yet.
                </Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 280 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ "& th": { backgroundColor: "#f8fafc", fontWeight: 700, fontSize: "0.78rem", py: 0.75, px: 1 } }}>
                        <TableCell>Invoice</TableCell>
                        <TableCell align="right">Allocated</TableCell>
                        <TableCell sx={{ width: 60 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {attachedByInvoice.map((row) => (
                        <TableRow key={row.invoiceID} sx={{ "& td": { py: 0.6, px: 1, fontSize: "0.82rem" } }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            <button onClick={() => setInvoiceDetailID(row.invoiceID)}
                              style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                                color: ZEPHYR_PURPLE, fontWeight: "inherit", fontSize: "inherit", textDecoration: "underline" }}>
                              {row.invoiceID}
                            </button>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(row.total)}</TableCell>
                          <TableCell align="right" sx={{ py: 0, whiteSpace: "nowrap" }}>
                            <Tooltip title="Edit amounts">
                              <IconButton size="small"
                                onClick={() => {
                                  const initial = {};
                                  for (const [scriptID, s] of attachedScripts) {
                                    if (s.invoiceID === row.invoiceID) initial[scriptID] = String(Number(s.allocatedAmount).toFixed(2));
                                  }
                                  setUnderpaidInitialAmounts(initial);
                                  setUnderpaidInvoiceID(row.invoiceID);
                                }}
                                sx={{ color: ZEPHYR_BLUE, p: 0.5 }}>
                                <EditOutlinedIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                            <IconButton size="small" onClick={() => removeInvoiceGroup(row.invoiceID)} sx={{ color: "#dc2626", p: 0.5 }}>
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Divider />
              <Box sx={{ px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                {[["Allocated", fmt(allocatedTotal), ZEPHYR_PURPLE], ["Total", fmt(totalAmount), "#1a1a1a"]].map(([label, val, col]) => (
                  <Box key={label} sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>{label}</Typography>
                    <Typography fontWeight={700} sx={{ color: col, fontSize: "0.85rem" }}>{val}</Typography>
                  </Box>
                ))}
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>Remaining</Typography>
                  <Typography fontWeight={700} sx={{ fontSize: "0.85rem", color: approxEqual(remaining, 0) ? "#16a34a" : remaining < 0 ? "#dc2626" : "#d97706" }}>
                    {fmt(remaining)}
                  </Typography>
                </Box>
                {balanced && (
                  <Chip label="Balanced ✓" size="small"
                    sx={{ backgroundColor: "#dcfce7", color: "#16a34a", fontWeight: 700, mt: 0.5, fontSize: "0.75rem" }} />
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ── Sticky footer ─────────────────────────────────────────── */}
        <Box sx={{
          flexShrink: 0, borderTop: "1px solid #e2e8f0", px: 3, py: 1.5,
          display: "flex", gap: 1.5, alignItems: "center", justifyContent: "flex-end",
          flexWrap: "wrap", backgroundColor: "#f8fafc",
        }}>
          <Button onClick={onClose} sx={{ textTransform: "none", color: "#64748b" }}>Cancel</Button>
          <ZBtn variant="outlined" onClick={() => handleSubmitClick("again-same-payer")} disabled={attachedScripts.size === 0}>
            Submit — same payer
          </ZBtn>
          <ZBtn variant="outlined" onClick={() => handleSubmitClick("again")} disabled={attachedScripts.size === 0}>
            Submit and start again
          </ZBtn>
          <ZBtn onClick={() => handleSubmitClick("close")} disabled={attachedScripts.size === 0}>
            Submit and close
          </ZBtn>
        </Box>
      </Drawer>

      {/* Sub-modals */}
      <InvoiceDetailModal open={invoiceDetailID !== null} invoiceID={invoiceDetailID}
        onClose={() => setInvoiceDetailID(null)} />
      <UnderpaidModal open={underpaidInvoiceID !== null} invoiceID={underpaidInvoiceID}
        initialAmounts={underpaidInitialAmounts}
        onClose={() => setUnderpaidInvoiceID(null)} onConfirm={handleUnderpaidConfirm} />
      <PendingScriptsModal open={pendingOpen}
        insCompID={header.payerType === "insurer" ? header.payerId : null}
        employerID={header.payerType === "employer" ? header.payerId : null}
        attachedScripts={attachedScripts}
        onClose={() => setPendingOpen(false)} onAddScript={handleAddPendingScript} />
      {automatchInvoices && (
        <AutomatchModal open={Boolean(automatchInvoices)} matchedInvoices={automatchInvoices}
          onClose={() => setAutomatchInvoices(null)}
          onConfirm={async () => {
            const subset = automatchInvoices;
            setAutomatchInvoices(null);
            for (const inv of subset) await addFullInvoice(inv);
          }} />
      )}
      <ReconcileModal open={reconcileOpen} totalAmount={totalAmount} allocatedTotal={allocatedTotal}
        payerName={selectedPayerName}
        payDate={header.remittanceDate}
        referenceNumber={header.referenceNumber || null}
        onClose={() => setReconcileOpen(false)} onConfirm={() => doSubmit(pendingSubmitAction ?? "close")}
        loading={submitLoading} />
    </>
  );
}

// ─── Landing tab content ──────────────────────────────────────────────────────

export default function ZephyrRemittancePage() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  return (
    <Box>
      <Box sx={{
        display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2,
        p: 3, mb: 3,
        background: `linear-gradient(135deg, ${ZEPHYR_LIGHT} 0%, #dbeafe 100%)`,
        borderRadius: 2, border: `1px solid ${ZEPHYR_BLUE}44`,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 2,
            background: `linear-gradient(135deg, ${ZEPHYR_BLUE}, ${ZEPHYR_PURPLE})`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <ReceiptOutlinedIcon sx={{ color: "#fff", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography fontWeight={700} fontSize="1.1rem" sx={{ color: ZEPHYR_PURPLE }}>Zephyr Claims</Typography>
            <Typography variant="body2" color="text.secondary">Remittance reconciliation for insurer and employer payments</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => setDrawerOpen(true)}
          sx={{
            textTransform: "none", fontWeight: 700, px: 3, py: 1.2,
            background: `linear-gradient(135deg, ${ZEPHYR_PURPLE}, ${ZEPHYR_BLUE})`,
            boxShadow: `0 2px 8px ${ZEPHYR_PURPLE}55`,
            "&:hover": { background: `linear-gradient(135deg, #1a0a2e, ${ZEPHYR_PURPLE})` },
          }}>
          Reconcile Payment
        </Button>
      </Box>
      <RemittanceDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </Box>
  );
}
